import { useState, useEffect } from "react";
import "./blog.scss";
import { useGetBlogPostsQuery } from "../features/api/blogApiSlice";
import FuelLoading from "./FuelLoading";
import PageHeader from "../components/PageHeader/PageHeader";
import Section from "../components/ui/Section";

// Categories matching your database schema options
const CATEGORIES = [
  { id: "all", label: "All Stories", value: "" },
  { id: "trips", label: "Road Trips", value: "Road Trips" },
  { id: "parks", label: "National Parks", value: "National Parks" },
  { id: "gear", label: "Gear Reviews", value: "Gear Reviews" },
  { id: "car", label: "Small Car Life", value: "Small Car Life" },
];

export default function BlogFeed() {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  // NEW: Track the currently selected category filter
  const [selectedCategory, setSelectedCategory] = useState("");
  const limit = 6;

  // ===== NEW AUTOMATIC SCROLL-TO-TOP CHASSIS ENGINE =====
  useEffect(() => {
    // Smoothly snaps the browser viewport back to coordinates (0,0) whenever page indices update
    window.scrollTo({
      top: 0,
      behavior: "smooth", // Gives it an elegant, sliding glide transition look
    });
  }, [page]); // <-- Listens closely and fires instantly whenever 'page' changes!

  // Reset to page 1 whenever the user types a new search query or changes categories
  useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedCategory]);

  // Pass all active filters to your RTK Query hook
  const { data, isLoading, isError } = useGetBlogPostsQuery({
    page,
    limit,
    search: searchTerm,
    category: selectedCategory, // NEW: Added category parameter down the pipe
    adminMode: false,
  });

  const posts = data?.items || [];
  const totalPages = data?.totalPages || 1;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisibleButtons = 5;

    if (totalPages <= maxVisibleButtons) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      pageNumbers.push(1);
      if (page > 3) pageNumbers.push("...");
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      for (let i = start; i <= end; i++) {
        if (!pageNumbers.includes(i)) pageNumbers.push(i);
      }
      if (page < totalPages - 2) pageNumbers.push("...");
      if (!pageNumbers.includes(totalPages)) pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  if (isLoading) {
    return (
      <div className="blog-loading">
        <FuelLoading text="Mapping out journals..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="blog-error">
        ⚠️ Unable to draw blog entries from the server.
      </div>
    );
  }

  return (
    <main className="blog-feed-page fuel-fade-in">
      <Section>
        <PageHeader
          title="Travel Logs"
          subtitle="Stories from the slow lane—national parks, backroads, and unexpected
          highway turns."
        />

        <div className="blog-feed-container">
          {/* SEARCH FILTERS INPUT HEADER BAR */}
          <div className="blog-search-zone">
            <input
              type="text"
              placeholder="🔍 Search articles by location, park, or keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="blog-search-input"
            />
          </div>

          {/* ===== NEW CATEGORY SELECTION TABS ROW ===== */}
          <nav
            className="blog-categories-row"
            aria-label="Filter stories by category"
          >
            {CATEGORIES.map((cat) => {
              const isActive = selectedCategory === cat.value;
              return (
                <button
                  key={cat.id}
                  type="button"
                  className={`category-tab-btn ${isActive ? "active" : ""}`}
                  onClick={() => setSelectedCategory(cat.value)}
                >
                  {cat.label}
                </button>
              );
            })}
          </nav>

          {posts.length === 0 ? (
            <p className="blog-empty-text">
              {searchTerm || selectedCategory
                ? "No road articles found matching your selected filters."
                : "No articles have been posted down this road yet. 🛣️"}
            </p>
          ) : (
            <>
              {/* ARTICLES FLUID MATRIX GRID */}
              <div className="blog-grid">
                {posts.map((post) => (
                  <article key={post._id} className="blog-card">
                    <div className="blog-card__thumb">
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        loading="lazy"
                      />
                      <span className="blog-card__category">
                        {post.category}
                      </span>
                    </div>
                    <div className="blog-card__body">
                      <div className="blog-card__meta">
                        <time>{formatDate(post.createdAt)}</time>
                        {/* FIXED: Renders the backend calculated reading length badge */}
                        <span className="read-time-tag">
                          ⏱️ {post.readingTime || 1} min read
                        </span>
                      </div>
                      <h2>{post.title}</h2>
                      <p>{post.excerpt}</p>
                      <a
                        href={`/blog/${post.slug}`}
                        className="blog-card__read-link"
                      >
                        Read Entry →
                      </a>
                    </div>
                  </article>
                ))}
              </div>

              {/* NUMBERED NUMERIC PAGINATION NAVIGATION CONTAINER */}
              {totalPages > 1 && (
                <nav
                  className="blog-num-pagination"
                  aria-label="Blog navigation options"
                >
                  <button
                    type="button"
                    className="page-nav-arrow"
                    disabled={page === 1}
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  >
                    ◀ Prev
                  </button>

                  <div className="page-numbers-row">
                    {getPageNumbers().map((num, idx) => {
                      if (num === "...") {
                        return (
                          <span key={`dots-${idx}`} className="page-dots">
                            ...
                          </span>
                        );
                      }
                      return (
                        <button
                          key={`page-${num}`}
                          type="button"
                          className={`page-num-btn ${
                            page === num ? "active" : ""
                          }`}
                          onClick={() => setPage(num)}
                        >
                          {num}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    className="page-nav-arrow"
                    disabled={page === totalPages}
                    onClick={() =>
                      setPage((prev) => Math.min(prev + 1, totalPages))
                    }
                  >
                    Next ▶
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </Section>
    </main>
  );
}
